import React, { useState } from "react";
import { Flag, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

/**
 * ReportModal — universal "Report content / user" surface. Wraps
 * around a small trigger button (also exported as `ReportButton`) and
 * opens a modal collecting the reason + optional details.
 *
 * Usage:
 *   <ReportButton targetType="video" targetId={video.id} />
 *   <ReportButton targetType="user" targetId={user.id} label="Report" />
 *
 * `target_type` supported by the backend at POST /api/reports:
 *   video · stream · user · comment · post · reel · story
 */
const REASONS = [
  "Spam / misleading",
  "Sexual or explicit content",
  "Hate speech / harassment",
  "Violence or self-harm",
  "Copyright infringement",
  "Impersonation / scam",
  "Something else",
];

export function ReportButton({ targetType = "video", targetId, label = "Report", className = "" }) {
  const [open, setOpen] = useState(false);
  if (!targetId) return null;
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid={`report-btn-${targetType}-${targetId}`}
        className={className || "inline-flex items-center gap-1 text-white/60 hover:text-red-400 text-xs font-bold uppercase tracking-wider"}
        title="Report to moderators"
      >
        <Flag className="w-3.5 h-3.5" /> {label}
      </button>
      {open && (
        <ReportModal
          targetType={targetType}
          targetId={targetId}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

export default function ReportModal({ targetType, targetId, onClose }) {
  const [reason, setReason] = useState("Spam / misleading");
  const [details, setDetails] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!reason) { toast.error("Pick a reason."); return; }
    setSubmitting(true);
    try {
      await api.post("/reports", {
        target_type: targetType,
        target_id: targetId,
        reason,
        details: details.slice(0, 800),
      });
      toast.success("Reported — thanks. Our team reviews within 24 hours.");
      onClose?.();
    } catch (err) {
      toast.error(formatApiError(err) || "Couldn't submit report.");
    } finally { setSubmitting(false); }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="report-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-center justify-center px-4"
    >
      <div className="w-full max-w-md bg-[#0d0f18] border border-white/10 rounded-2xl p-5 text-white">
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="inline-flex items-center gap-1 text-red-400 text-[10px] uppercase tracking-widest font-black mb-0.5">
              <Flag className="w-3 h-3" /> Report
            </div>
            <h2 className="font-display font-black text-xl tracking-tight">What&apos;s wrong here?</h2>
            <p className="text-white/50 text-xs mt-0.5">Reports are anonymous to the creator. Our team reviews within 24 hours.</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="report-close"
            className="text-white/50 hover:text-white p-1 -mr-1"
            aria-label="Close"
          ><X className="w-5 h-5" /></button>
        </div>

        <div className="space-y-2 mb-3">
          {REASONS.map((r) => (
            <label
              key={r}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition ${
                reason === r ? "bg-red-500/10 border-red-500/50" : "bg-white/[0.03] border-white/10 hover:bg-white/[0.06]"
              }`}
            >
              <input
                type="radio"
                name="report-reason"
                checked={reason === r}
                onChange={() => setReason(r)}
                data-testid={`report-reason-${r.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                className="accent-red-500"
              />
              <span className="text-sm">{r}</span>
            </label>
          ))}
        </div>

        <textarea
          value={details}
          onChange={(e) => setDetails(e.target.value)}
          maxLength={800}
          rows={3}
          placeholder="Optional — add context (link, timestamp, what you saw)"
          data-testid="report-details"
          className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-red-500/60 placeholder:text-white/30 resize-none"
        />

        <div className="mt-4 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            data-testid="report-cancel"
            className="text-white/60 hover:text-white text-xs font-bold uppercase tracking-wider px-4 py-2"
          >Cancel</button>
          <button
            type="button"
            onClick={submit}
            disabled={submitting}
            data-testid="report-submit"
            className="bg-red-500 hover:bg-red-600 text-white font-black text-xs uppercase tracking-wider px-5 py-2 rounded-full disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Flag className="w-3.5 h-3.5" />}
            {submitting ? "Sending…" : "Submit report"}
          </button>
        </div>
      </div>
    </div>
  );
}
